export const TICK_RATE = 60;
export const TICK_MS = 1000 / TICK_RATE;
export const SHOT_CLOCK_TICKS = 8 * TICK_RATE;
export const CHECK_BALL_TICKS = 42;
export const GATHER_SET_TICKS = 14;
export const POSSESSION_COUNT = 8;

export type PlayerId = "o0" | "o1" | "o2";
export type DefenderId = "d0" | "d1" | "d2";

export interface Vec2 {
  x: number;
  y: number;
}

export type RouteKind =
  | "hold"
  | "drift"
  | "lift"
  | "shallow-cut"
  | "exchange"
  | "relocate";

export interface RouteSpec {
  kind: RouteKind;
  origin: Vec2;
  target: Vec2;
  periodTicks: number;
  phaseTicks: number;
  sway: number;
}

export interface OffensivePlayerSpec {
  id: PlayerId;
  route: RouteSpec;
}

export interface DefenderSpec {
  id: DefenderId;
  assignment: PlayerId;
  start: Vec2;
  reactionTicks: number;
  speedPerTick: number;
  armLength: number;
  /** v2: maximum per-tick change of velocity, preserving readable closeout momentum. */
  accelerationPerTick?: number;
  /** v2: how far this defender commits when selected as the visible pass helper. */
  helpCommitment?: number;
  /** v2: ticks after a catch that the helper finishes the stunt before recovering. */
  stuntTicks?: number;
}

export type Difficulty = "flow" | "mixed" | "scramble" | "climax";

export interface PossessionSpec {
  id: string;
  index: number;
  label: string;
  difficulty: Difficulty;
  ballhandler: PlayerId;
  offense: readonly OffensivePlayerSpec[];
  defense: readonly DefenderSpec[];
  noviceFloor: boolean;
  climax: boolean;
  secondPassFocus: boolean;
}

export interface DailyCourt {
  generatorVersion: number;
  utcDate: string;
  puzzleNumber: number;
  seed: number;
  profile: Exclude<Difficulty, "climax">;
  possessions: readonly PossessionSpec[];
}

export interface DailyCourtOptions {
  utcDate?: string;
  count?: number;
  generatorVersion?: number;
  launchEpoch?: string;
}

export interface PlayerSnapshot {
  id: PlayerId;
  position: Vec2;
  velocity: Vec2;
  hasBall: boolean;
  gathering: boolean;
  gatherProgress: number;
  callingForBall: boolean;
}

export type DefenderHelpState = "home" | "stunting" | "recovering";

export interface DefenderSnapshot {
  id: DefenderId;
  assignment: PlayerId;
  position: Vec2;
  velocity: Vec2;
  hand: Vec2;
  contesting: boolean;
  helpState: DefenderHelpState;
  helpingFrom: PlayerId | null;
}

export interface BallSnapshot {
  position: Vec2;
  owner: PlayerId | null;
  passingTo: PlayerId | null;
  passProgress: number;
}

export type RunPhase = "check" | "live" | "resolved" | "complete";
export type ShotResult = "make" | "rushed" | "altered" | "expired";

export interface PassRecord {
  possessionIndex: number;
  from: PlayerId;
  to: PlayerId;
  launchedTick: number;
  caughtTick: number;
  clockTicksAtLaunch: number;
  helperId?: DefenderId;
}

export interface ShotRecord {
  possessionIndex: number;
  shooter: PlayerId;
  value: 2 | 3 | null;
  result: ShotResult;
  made: boolean;
  points: number;
  releaseTick: number | null;
  clockTicksAtRelease: number;
  gatherTicks: number;
  defenderId: DefenderId | null;
  defenderDistance: number | null;
  handCorridorDistance: number | null;
  /** For "altered" results: whether the contesting hand was the on-ball closeout or a stunting help defender. */
  alteredBy: "closeout" | "help" | null;
}

export interface PossessionResult extends ShotRecord {
  passes: number;
}

export interface ScoreSnapshot {
  points: number;
  timeLeftTicks: number;
  timeLeftSeconds: number;
  tier: "Finding Rhythm" | "In Rhythm" | "Bucket Getter" | "Closer" | "Automatic";
}

export interface ShotPreview {
  value: 2 | 3;
  gatherTicks: number;
  set: boolean;
  nearestDefenderId: DefenderId;
  defenderDistance: number;
  hand: Vec2;
  corridorDistance: number;
  obstructed: boolean;
}

export interface ShotGeometryInput {
  gatherTicks: number;
  shooterPosition: Vec2;
  defenderHand: Vec2;
}

export interface ShotGeometryDecision {
  value: 2 | 3;
  set: boolean;
  corridorDistance: number;
  obstructed: boolean;
  made: boolean;
  result: "make" | "rushed" | "altered";
}

export interface GameSnapshot {
  phase: RunPhase;
  tick: number;
  possessionIndex: number;
  possessionCount: number;
  clockTicks: number;
  clockSeconds: number;
  ballhandler: PlayerId;
  offense: readonly PlayerSnapshot[];
  defense: readonly DefenderSnapshot[];
  ball: BallSnapshot;
  shotPreview: ShotPreview | null;
  score: ScoreSnapshot;
  passHistory: readonly PassRecord[];
  results: readonly PossessionResult[];
  lastResult: PossessionResult | null;
}

export interface ValidationIssue {
  possessionIndex: number | null;
  code: string;
  message: string;
}

export interface ValidationResult {
  valid: boolean;
  issues: readonly ValidationIssue[];
  reachableTwos: number;
  reachableThrees: number;
  liveChoices: number;
  timingSensitivePossessions: number;
  multiPassAdvantages: number;
  robustMultiPassPossessions: number;
  robustWindowPossessions: number;
  directPolicyPoints: number;
  onePassPolicyPoints: number;
  adaptivePolicyPoints: number;
  robustOnePassPolicyPoints: number;
  robustSinglePassPolicyPoints: number;
  robustAdaptivePolicyPoints: number;
  hasClimax: boolean;
}

export interface RunState {
  readonly court: DailyCourt;
  tick: number;
  possessionIndex: number;
  phase: RunPhase;
  phaseTicks: number;
  clockTicks: number;
  ballhandler: PlayerId;
  ball: BallSnapshot;
  offense: PlayerSnapshot[];
  defense: DefenderSnapshot[];
  gather: { shooter: PlayerId; startedTick: number; frozenAt: Vec2 } | null;
  pendingPass: {
    from: PlayerId;
    to: PlayerId;
    startedTick: number;
    durationTicks: number;
    start: Vec2;
    end: Vec2;
    historyIndex: number;
  } | null;
  passHistory: PassRecord[];
  results: PossessionResult[];
  transitionTicks: number;
}
